type RGBArray = [number, number, number]

/**
 * Parses a color string into RGB values.
 *
 * Supports multiple color formats including hex (3 and 6 digit) and RGB.
 * Returns an array of RGB values (0-255 range).
 *
 * @param input - Color string to parse
 * @returns Array of RGB values [red, green, blue]
 * @throws Error if color format is not recognized
 *
 * @example
 * ```typescript
 * parseColor('#fff')           // [255, 255, 255]
 * parseColor('#ff0000')        // [255, 0, 0]
 * parseColor('rgb(100,50,200)') // [100, 50, 200]
 *
 * parseColor('invalid')        // throws Error
 * ```
 */
export const parseColor = (input: string): RGBArray => {
  // parses: #fff
  let m = input.match(/^#([0-9a-f]{3})$/i)
  if (m && m[1]) {
    // in three-character format, each value is multiplied by 0x11 to give an
    // even scale from 0x00 to 0xff
    return [
      parseInt(m[1].charAt(0), 16) * 0x11,
      parseInt(m[1].charAt(1), 16) * 0x11,
      parseInt(m[1].charAt(2), 16) * 0x11,
    ]
  }

  // parses: #ffffff
  m = input.match(/^#([0-9a-f]{6})$/i)
  if (m && m[1]) {
    return [
      parseInt(m[1].substr(0, 2), 16),
      parseInt(m[1].substr(2, 2), 16),
      parseInt(m[1].substr(4, 2), 16),
    ]
  }

  // parses: rgb(105, 105, 105)
  m = input.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i)
  if (m && m[1] && m[2] && m[3]) {
    return [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])]
  }

  throw new Error('parseColor: Unknown color format: ' + input)
}

/**
 * Formats RGB values into a CSS rgb() string.
 *
 * Converts an array of RGB values into a CSS-compatible rgb() string.
 *
 * @param rgb - Array of RGB values [red, green, blue]
 * @returns CSS rgb() string
 *
 * @example
 * ```typescript
 * formatColor([255, 0, 0])     // 'rgb(255,0,0)'
 * formatColor([100, 150, 200]) // 'rgb(100,150,200)'
 * ```
 */
export const formatColor = (rgb: RGBArray): string => {
  return `rgb(${rgb.join(',')})`
}

export function relativeLuminance(rgb: RGBArray): number {
  // WCAG 2.1 luminance formula https://www.w3.org/TR/WCAG22/#relativeluminancedef
  let r = rgb[0] / 255
  let g = rgb[1] / 255
  let b = rgb[2] / 255
  r = r <= 0.04045 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4 // 0.04045 is WCAG 2.1 diff
  g = g <= 0.04045 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4
  b = b <= 0.04045 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Determines if a color is considered dark.
 *
 * Uses the W3C AERT algorithm to calculate brightness and determine
 * if a color is dark (brightness < 180). Useful for choosing appropriate
 * text colors or UI elements based on background color.
 *
 * @param color - Color string to analyze
 * @returns True if color is dark, false if light or invalid
 *
 * @example
 * ```typescript
 * isDark('#000000')    // true
 * isDark('#ffffff')    // false
 * isDark('#333333')    // true
 * isDark('#cccccc')    // false
 * isDark('invalid')    // false (graceful fallback)
 * ```
 */
export const isDark = (color: string): boolean => {
  let rgb
  try {
    rgb = parseColor(color)
  } catch {
    return false
  }
  // http://www.w3.org/TR/AERT#color-contrast
  const result = Math.round(
    (parseInt(rgb[0]) * 299 + parseInt(rgb[1]) * 587 + parseInt(rgb[2]) * 114) /
      1000,
  )
  return result < 180
}

/**
 * Gets the appropriate contrast color (black or white) for a given background.
 *
 * Returns white for dark backgrounds and black for light backgrounds
 * to ensure good text readability.
 *
 * @param color - Background color to get contrast for
 * @returns '#fff' for dark backgrounds, '#000' for light backgrounds
 *
 * @example
 * ```typescript
 * getContrastColor('#000000')  // '#fff' (white text on black)
 * getContrastColor('#ffffff')  // '#000' (black text on white)
 * getContrastColor('#333333')  // '#fff' (white text on dark gray)
 * ```
 */
export const getContrastColor = (color: string): string => {
  return isDark(color) ? '#fff' : '#000'
}

/**
 * Blends two colors together by a specified amount.
 *
 * Interpolates between two RGB colors using linear blending.
 * Blend value of 0 returns the first color, 1 returns the second color.
 *
 * @param from - Starting RGB color
 * @param to - Target RGB color
 * @param blend - Blend amount (0-1, where 0=from, 1=to)
 * @returns Blended RGB color
 *
 * @example
 * ```typescript
 * const red = [255, 0, 0]
 * const blue = [0, 0, 255]
 *
 * blendColors(red, blue, 0)    // [255, 0, 0] (pure red)
 * blendColors(red, blue, 1)    // [0, 0, 255] (pure blue)
 * blendColors(red, blue, 0.5)  // [128, 0, 128] (purple mix)
 * ```
 */
export const blendColors = (
  from: RGBArray,
  to: RGBArray,
  // blend=0..1
  blend: number,
): RGBArray => {
  return [
    Math.round(from[0] * (1 - blend) + to[0] * blend),
    Math.round(from[1] * (1 - blend) + to[1] * blend),
    Math.round(from[2] * (1 - blend) + to[2] * blend),
  ]
}

/**
 * Darkens a color by blending it with black.
 *
 * Creates a darker version of the input color by blending with black.
 * Higher amounts create darker colors.
 *
 * @param color - Color string to darken
 * @param amount - Amount to darken (0-1, where 1=completely black)
 * @returns Darkened color as CSS rgb() string
 *
 * @example
 * ```typescript
 * darkenColor('#ff0000', 0.2)  // Slightly darker red
 * darkenColor('#ff0000', 0.5)  // Much darker red
 * darkenColor('#ff0000', 1)    // Black
 * ```
 */
export const darkenColor = (color: string, amount: number): string => {
  const rgb = parseColor(color)
  return formatColor(blendColors(rgb, [0, 0, 0], amount))
}

/**
 * Lightens a color by blending it with white.
 *
 * Creates a lighter version of the input color by blending with white.
 * Higher amounts create lighter colors.
 *
 * @param color - Color string to lighten
 * @param amount - Amount to lighten (0-1, where 1=completely white)
 * @returns Lightened color as CSS rgb() string
 *
 * @example
 * ```typescript
 * lightenColor('#ff0000', 0.2)  // Slightly lighter red
 * lightenColor('#ff0000', 0.5)  // Much lighter red (pink)
 * lightenColor('#ff0000', 1)    // White
 * ```
 */
export const lightenColor = (color: string, amount: number): string => {
  const rgb = parseColor(color)
  return formatColor(blendColors(rgb, [255, 255, 255], amount))
}
